import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkle, EnvelopeSimple, ArrowLeft, CheckCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTheme } from '@/contexts/ThemeContext';
import ThemeToggle from '@/components/ThemeToggle';
import PageTransition from '@/components/PageTransition';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetUrl, setResetUrl] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setSent(true);
      
      // Afficher le lien de reset si l'email n'a pas été envoyé
      if (response.data.debug_reset_url) {
        setResetUrl(response.data.debug_reset_url);
      }
      
      if (response.data.email_sent) {
        toast.success(t('auth.instructionsSent'));
      } else {
        toast.info("Un lien de réinitialisation a été généré");
      }
    } catch (error) {
      const detail = error.response?.data?.detail || t('common.error');
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTransition>
      <div className={`min-h-screen flex items-center justify-center p-4 relative z-10 ${isDark ? '' : 'bg-gray-50'}`}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="flex justify-end mb-6">
            <ThemeToggle />
          </div>
          
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center shadow-lg shadow-violet-500/25">
                <Sparkle size={20} weight="fill" className="text-white" />
              </div>
              <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>SIMBLYO</span>
            </Link>
            <h1 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('auth.forgotPasswordTitle')}
            </h1>
            <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-600'}`}>
              {sent ? t('auth.checkYourEmail') : t('auth.forgotPasswordSubtitle')}
            </p>
          </div>

          <div className={`p-8 rounded-2xl ${
            isDark 
              ? 'glass-card' 
              : 'bg-white border border-gray-200 shadow-lg'
          }`}>
            {sent ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
                  <CheckCircle size={32} weight="fill" className="text-green-500" />
                </div>
                <div>
                  <p className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {t('auth.emailSent')}
                  </p>
                  <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                    {t('auth.emailSentDesc')} <strong>{email}</strong>, {t('auth.emailSentDesc2')}
                  </p>
                </div>
                
                {/* Lien de reset si email non envoyé */}
                {resetUrl && (
                  <div className={`mt-4 p-4 rounded-xl text-left ${isDark ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-100'}`}>
                    <p className={`text-xs font-medium mb-2 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                      ⚠️ L'email n'a pas pu être envoyé. Utilisez ce lien directement :
                    </p>
                    <Link 
                      to={resetUrl}
                      className="text-sm text-violet-500 hover:text-violet-400 underline break-all font-medium"
                    >
                      → Réinitialiser mon mot de passe
                    </Link>
                  </div>
                )}
                
                <div className="pt-4 space-y-3">
                  <Button
                    variant="outline"
                    className={`w-full ${isDark ? 'border-white/10 hover:bg-white/5' : ''}`}
                    onClick={() => { setSent(false); setEmail(''); setResetUrl(''); }}
                  >
                    {t('common.tryAnotherEmail')}
                  </Button>
                  <Link to="/login">
                    <Button className="w-full btn-premium">
                      {t('common.backToLogin')}
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className={isDark ? 'text-white/70' : 'text-gray-700'}>
                    {t('auth.email')}
                  </Label>
                  <div className="relative">
                    <EnvelopeSimple className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-white/30' : 'text-gray-400'}`} size={18} />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="votre@email.com"
                      required
                      className={`pl-10 ${
                        isDark 
                          ? 'bg-white/[0.03] border-white/[0.08] focus:border-violet-500/50' 
                          : 'bg-white border-gray-200'
                      }`}
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full btn-premium"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? t('common.sending') : t('common.sendLink')}
                </Button>

                <div className="text-center">
                  <Link 
                    to="/login" 
                    className={`inline-flex items-center gap-1 text-sm hover:underline ${isDark ? 'text-white/50 hover:text-white/70' : 'text-gray-600 hover:text-gray-800'}`}
                  >
                    <ArrowLeft size={14} />
                    {t('common.backToLogin')}
                  </Link>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </PageTransition>
  );
}
